import ENV from '../config/environment';
import { numberToUserDisplay } from '@ebiz/pretty-num';
import { camelize } from '@ember/string';
import { isNearInfinity } from '../utils/common';
import { getURL } from '../utils/resource';
import { capitalizeFirstLetter, uncapitalizeFirstLetter } from '../utils/format';
import FilterConstants from '../filter-constants';
import type IntlService from 'ember-intl/services/intl';
import * as AditoolsSpice from '@ebiz/aditools-spice/schematics';
import type IndexModel from '../models/index';
import type IndividualStageInput from '../models/inputs/component-selection/individual-stage';
import type StageDetailResults from '../models/results/sub-results/stage-details';
import type CircuitDetailResults from '../models/results/sub-results/circuit-details';
import type AssetsService from '../services/assets';
import type LoggerService from '../services/logger';
import type FastBoot from 'ember-cli-fastboot/services/fastboot';
import * as AditoolsSpiceParts from '@ebiz/aditools-spice/data/opamps.json';

type SchematicFileList = Record<string, SchematicFileInfo>;

type SchematicFileInfo = {
  stage: number;
  type: string;
  name: string;
  data: SchematicContent;
};

type SchematicContent = {
  flag: boolean;
  content: string | null;
};

type SpicePartsDownloadType = {
  partName: string;
  type: string;
};

type SpiceSchematicObjectList = Record<string, AditoolsSpice.SpiceSchematic>;

const mergePartsArray = function (
  partArray1: Array<SpicePartsDownloadType>,
  partArray2: Array<SpicePartsDownloadType>,
  mergeKey1: string,
  mergeKey2: string,
) {
  // Build list of Parts that contain Netlist, LTspice, or both
  let listOfParts: Array<SpicePartsDownloadType> = [];
  listOfParts = listOfParts.concat(partArray1);
  for (let i = 0; i < partArray2.length; i++) {
    const item = partArray2[i];
    let bFound = false;
    for (let j = 0; j < listOfParts.length; j++) {
      if (item?.partName === listOfParts[j]?.partName) {
        bFound = true;
        const part = listOfParts[j];
        if (part && part.type && part.type.indexOf(mergeKey1) >= 0) {
          part.type = mergeKey1 + '/' + mergeKey2;
          listOfParts[j] = part;
        }
        break;
      }
    }
    if (!bFound && item) {
      listOfParts.push(item);
    }
  }
  return listOfParts;
};

export async function getSpiceSchematicFiles(
  pathURL: string,
  downloadType: string,
  model: IndexModel,
  assetService: AssetsService,
  loggerService: LoggerService,
  fastbootService: FastBoot,
  intlService: IntlService,
  netlistParts: Array<SpicePartsDownloadType>,
): Promise<Array<DownloadFile>> {
  let downloadFiles: Array<DownloadFile> = [];
  const schematicParts: Array<SpicePartsDownloadType> = [];
  const spiceParts: Record<string, AditoolsSpice.SpicePartInfo> = AditoolsSpiceParts;
  if (spiceParts) {
    let generateSchematic = true;
    const opAmpsList = [
      ...new Set(
        model.inputs.components.individualStageInputs.map((item: IndividualStageInput) => {
          return item.opAmp;
        }),
      ),
    ] as Array<string>;
    for (let i = 0; i < opAmpsList.length; i++) {
      const opAmp: string = opAmpsList[i] as string;
      if (spiceParts && typeof spiceParts[opAmp] !== 'undefined') {
        schematicParts.push({ partName: opAmp, type: 'LTspice' }); // This part has a spice resource
      } else {
        generateSchematic = false;
      }
    }
    if (generateSchematic) {
      const schematicFileList = getSchematicTemplates(
        schematicParts,
        spiceParts,
        model,
        loggerService,
        fastbootService,
        assetService,
      );
      downloadFiles = populateSchematicFiles(await schematicFileList, spiceParts, model, downloadType);
    }
    downloadFiles = downloadFiles.concat(
      generateReadMeFile(
        opAmpsList,
        netlistParts,
        schematicParts,
        downloadFiles !== null && downloadFiles.length > 0,
        downloadType,
        intlService,
      ),
    );
  } else {
    throw 'Tool Config Data is Unavailable.';
  }
  return downloadFiles;
}

async function getSchematicTemplates(
  listOfParts: Array<{ partName: string; type: string }>,
  spiceParts: Record<string, AditoolsSpice.SpicePartInfo>,
  model: IndexModel,
  loggerService: LoggerService,
  fastbootService: FastBoot,
  assetService: AssetsService,
): Promise<SchematicFileList> {
  const schematicFileList: SchematicFileList = {},
    contentPath = getURL(ENV.rootURL + FilterConstants.spiceCircuitPath, fastbootService, loggerService),
    detailsOfStages = model.results.components.detailsOfStages,
    detailsOfCircuits = model.results.tolerances.detailsOfCircuits,
    count = detailsOfStages.length;
  let content: string | null;

  // Get ASC Templates for AC, Noise and Transient Analysis
  content = await assetService.getAsset(`${contentPath}acAnalysis${count}Stage.txt`, 'TEXT');
  schematicFileList[FilterConstants.schematicKey.acKey] = {
    stage: 0,
    type: 'asc',
    name: 'ACAnalysis.asc',
    data: { flag: content !== null && content.length > 0, content: content },
  };
  content = await assetService.getAsset(`${contentPath}noiseAnalysis${count}Stage.txt`, 'TEXT');
  schematicFileList[FilterConstants.schematicKey.noiseKey] = {
    stage: 0,
    type: 'asc',
    name: 'NoiseAnalysis.asc',
    data: { flag: content !== null && content.length > 0, content: content },
  };
  content = await assetService.getAsset(`${contentPath}transientAnalysis${count}Stage.txt`, 'TEXT');
  schematicFileList[FilterConstants.schematicKey.transientKey] = {
    stage: 0,
    type: 'asc',
    name: 'TransientAnalysis.asc',
    data: { flag: content !== null && content.length > 0, content: content },
  };

  // Build asc and asy files per part
  for (let i = 0; i < count; i++) {
    const stageData = detailsOfStages[i],
      cirData = detailsOfCircuits[i],
      opAmpName: string = stageData.actualStage.opAmp,
      stageImplementation = stageData.implementation.replace(/ /g, ''),
      stageType = camelize(stageData.filterName.substring(10).replace(/_/g, '-')),
      stageLetter = stageData.stageLetter,
      hasGain = stageData.gain.target > 1,
      stage = i + 1;
    let name =
        'Stage' + stageLetter + '_' + capitalizeFirstLetter(stageType) + capitalizeFirstLetter(stageImplementation),
      path = contentPath + 'subckts/' + uncapitalizeFirstLetter(stageType) + capitalizeFirstLetter(stageImplementation); //Yes, this matters

    if (stageImplementation === FilterConstants.implementationType.deliyannisFriend) {
      if (cirData.r3.value === 0 && isNearInfinity(cirData.r4.value)) {
        path += 'NoPosFB';
        name += 'NoPosFB';
      } else if (isNearInfinity(cirData.r2.value)) {
        path += 'PosFBnoR2';
        name += 'PosFBnoR2';
      } else {
        path += 'PosFB';
        name += 'PosFB';
      }
    } else {
      if (!(stageImplementation === FilterConstants.implementationType.multipleFeedback)) {
        const gainString = hasGain ? 'Gain' : '';
        path += gainString;
        name += gainString;
      }
    }

    // Append the file type to the path
    path += '.txt';

    // Append the part# to the name
    name += '_' + (spiceParts && spiceParts[opAmpName] ? spiceParts[opAmpName]?.spicePartNumber : '');

    content = await assetService.getAsset(path, 'TEXT');
    schematicFileList['asc' + stage] = {
      stage: stage,
      type: 'asc',
      name: name + '.asc',
      data: { flag: content !== null && content.length > 0, content: content },
    };
    content = await assetService.getAsset(`${contentPath}subckts/asyTemplate.txt`, 'TEXT');
    schematicFileList['asy' + stage] = {
      stage: stage,
      type: 'asy',
      name: name + '.asy',
      data: { flag: content !== null && content.length > 0, content: content },
    };
  }
  return schematicFileList;
}

function populateSchematicFiles(
  schematicFileList: SchematicFileList,
  spiceParts: Record<string, AditoolsSpice.SpicePartInfo>,
  model: IndexModel,
  downloadType: string,
): Array<DownloadFile> {
  const result: Array<DownloadFile> = [];
  const spiceSchematicObjectList: SpiceSchematicObjectList = {};
  let status = true;

  Object.keys(schematicFileList).forEach((key) => {
    const schematicFileInfo: SchematicFileInfo = schematicFileList[key] as SchematicFileInfo;
    status = status && schematicFileInfo.data !== null && schematicFileInfo.data.flag;
    const template =
      schematicFileInfo.data !== null && schematicFileInfo.data.content !== null ? schematicFileInfo.data.content : '';
    spiceSchematicObjectList[key] = new AditoolsSpice.SpiceSchematic(spiceParts, template);
  });

  if (status) {
    // Begin putting together spice schematics data.
    // Input Signal
    updateInputSignal(spiceSchematicObjectList, model);

    // Replace Simulation Directives
    updateSimulationDirectives(spiceSchematicObjectList, model);

    // Voltage Suppplies
    updateVoltageSupplies(spiceSchematicObjectList, model, spiceParts);

    const detailsOfStages = model.results.components.detailsOfStages,
      detailsOfCircuits = model.results.tolerances.detailsOfCircuits;

    for (const item in schematicFileList) {
      if (Object.prototype.hasOwnProperty.call(schematicFileList, item)) {
        const schematicFile = schematicFileList[item];
        const schematicObject = spiceSchematicObjectList[item];
        if (schematicFile && schematicFile.stage > 0) {
          // All subsckts.
          const stageDetail = detailsOfStages[schematicFile.stage - 1],
            circuitDetail = detailsOfCircuits[schematicFile.stage - 1],
            opAmpName = stageDetail.actualStage.opAmp,
            stageImplementation = stageDetail.implementation.replace(/ /g, ''),
            stageType = camelize(stageDetail.filterName.substring(10).replace(/_/g, '-')),
            spicePart = spiceParts[opAmpName];

          if (schematicFile.type !== 'asy') {
            const schematicValues = circuitDetail ? getSchematicValues(circuitDetail) : {};
            // Update the values for the component elements and stages
            for (const item in schematicValues) {
              if (Object.prototype.hasOwnProperty.call(schematicValues, item)) {
                const itemValue = schematicValues[item];
                if (itemValue) {
                  schematicObject?.updateSymbolAttribute(
                    item,
                    'Value',
                    typeof itemValue === 'number'
                      ? numberToUserDisplay(itemValue, 'Spice', 3, { showZeroes: false })
                      : itemValue,
                  );
                }
              }
            }
            // Update OpAmp
            if (spicePart) {
              const rails = { '+Rail': 'V+', '-Rail': 'V-' };
              schematicObject?.updateSymbol('U1', opAmpName, true, rails);
            }
          } else {
            const spicePart = spiceParts[opAmpName];
            if (schematicObject && spicePart) {
              spiceSchematicObjectList[item] = updateStageInfo(
                schematicObject.schematic,
                spicePart.spicePartNumber,
                stageType,
                stageImplementation,
                spiceParts,
              );
            }
          }
        } else {
          // Set the subckt for acKey, noiseKey, or transientKey
          if (schematicObject) {
            spiceSchematicObjectList[item] = updateSubCircuitInfo(
              schematicObject.schematic,
              detailsOfStages,
              detailsOfCircuits,
              spiceParts,
            );
          }
        }
      }
    }

    // Build list of Files to Download
    Object.keys(spiceSchematicObjectList).forEach((key) => {
      const schematicFileInfo: SchematicFileInfo = schematicFileList[key] as SchematicFileInfo;
      result.push({
        name: schematicFileInfo.name,
        value: spiceSchematicObjectList[key]?.schematic,
        location: downloadType === 'all' ? 'SPICE Files\\LTspice' : 'LTspice',
        type: 'LTSPICE',
      });
    });
  }
  return result;
}

function updateSimulationDirectives(spiceSchematicObjectList: SpiceSchematicObjectList, model: IndexModel): void {
  spiceSchematicObjectList[FilterConstants.schematicKey.acKey]?.updateACAnalysis({
    sweepType: 'decade',
    points: 100,
    startFrequency: model.results.specifications.filterFrequencyRange.startFrequency,
    stopFrequency: model.results.specifications.filterFrequencyRange.stopFrequency,
  });
  spiceSchematicObjectList[FilterConstants.schematicKey.noiseKey]?.updateNoiseAnalysis({
    sweepType: 'decade',
    points: 100,
    startFrequency: model.results.specifications.filterFrequencyRange.startFrequency,
    stopFrequency: model.results.specifications.filterFrequencyRange.stopFrequency,
  });
  spiceSchematicObjectList[FilterConstants.schematicKey.transientKey]?.updateTransientAnalysis({
    stopTime: Math.max(...model.results.specifications.timeAxis),
  });
}

function updateInputSignal(spiceSchematicObjectList: SpiceSchematicObjectList, model: IndexModel): void {
  const voltageRangeObject = model.results.components.voltageRange.detailsOfVoltageRange;
  if (voltageRangeObject !== null && voltageRangeObject.length > 0) {
    const minInput = voltageRangeObject[0].nominalVoltageDisplay.maxSignals.inputRange.minimum;
    const maxInput = voltageRangeObject[0].nominalVoltageDisplay.maxSignals.inputRange.maximum;
    const maxFreq = voltageRangeObject[0].nominalVoltageDisplay.maxSignals.inputRange.frequency;

    // calculate average
    const iSignalMid = (minInput + maxInput) / 2;

    // calculate amplitude
    const iSignalAmplitude = (maxInput - minInput) / 2;

    spiceSchematicObjectList[FilterConstants.schematicKey.acKey]?.updateSineVoltageSource('VIN', {
      offset: iSignalMid,
      peakAmplitude: iSignalAmplitude,
      frequency: maxFreq,
    });
    spiceSchematicObjectList[FilterConstants.schematicKey.noiseKey]?.updateSineVoltageSource('VNOISE', {
      offset: iSignalMid,
      peakAmplitude: iSignalAmplitude,
      frequency: maxFreq,
    });
    spiceSchematicObjectList[FilterConstants.schematicKey.transientKey]?.updatePulseVoltageSource('VIN', {
      lowValue: iSignalMid,
      highValue: maxInput,
      delayTime: 0,
      riseTime: 0.000000001,
      fallTime: 0.000000001,
      pulseWidth: 1,
      period: 2,
    });
  }
}

function updateVoltageSupplies(
  spiceSchematicObjectList: SpiceSchematicObjectList,
  model: IndexModel,
  spiceParts: Record<string, AditoolsSpice.SpicePartInfo>,
): void {
  [
    FilterConstants.schematicKey.acKey,
    FilterConstants.schematicKey.noiseKey,
    FilterConstants.schematicKey.transientKey,
  ].forEach((key) => {
    let schematicContent = spiceSchematicObjectList[key]?.schematic;
    if (schematicContent) {
      schematicContent = schematicContent.replace('{Vp}', model.inputs.components.vsPlus.toString());
      schematicContent = schematicContent.replace('{Vn}', model.inputs.components.vsMinus.toString());
      schematicContent = schematicContent.replace(
        '{Vref}',
        (model.results.components.needReferenceStage ? model.inputs.components.vsPlus / 2 : 0).toString(),
      );
      spiceSchematicObjectList[key] = new AditoolsSpice.SpiceSchematic(spiceParts, schematicContent);
    }
  });
}

function updateStageInfo(
  schematicContentIn: string,
  partNumber: string,
  stageType: string,
  implementation: string,
  spiceParts: Record<string, AditoolsSpice.SpicePartInfo>,
): AditoolsSpice.SpiceSchematic {
  let schematicContent = schematicContentIn;
  schematicContent = schematicContent.replace('{partNumber}', partNumber);
  schematicContent = schematicContent.replace('{stageType}', capitalizeFirstLetter(stageType));
  schematicContent = schematicContent.replace('{implementation}', capitalizeFirstLetter(implementation));
  return new AditoolsSpice.SpiceSchematic(spiceParts, schematicContent);
}

function updateSubCircuitInfo(
  schematicContentIn: string,
  detailsOfStages: Array<StageDetailResults>,
  detailsOfCircuits: Array<CircuitDetailResults>,
  spiceParts: Record<string, AditoolsSpice.SpicePartInfo>,
): AditoolsSpice.SpiceSchematic {
  let schematicContent = schematicContentIn;
  const count = detailsOfStages.length;
  for (let i = 0; i < count; i++) {
    const stageData = detailsOfStages[i],
      cirData = detailsOfCircuits[i],
      opAmpName = cirData?.opAmp.name,
      stageImplementation = stageData?.implementation.replace(/ /g, ''),
      stageType = camelize(stageData?.filterName.substring(10).replace(/_/g, '-')),
      stageLetter = stageData?.stageLetter;
    let name =
      'Stage' + stageLetter + '_' + capitalizeFirstLetter(stageType) + capitalizeFirstLetter(stageImplementation);

    if (stageImplementation === FilterConstants.implementationType.deliyannisFriend) {
      if (cirData) {
        if (cirData.r3.value === 0 && isNearInfinity(cirData.r4.value)) name += 'NoPosFB';
        else if (isNearInfinity(cirData.r2.value)) name += 'PosFBnoR2';
        else name += 'PosFB';
      }
    } else {
      if (!(stageImplementation === FilterConstants.implementationType.multipleFeedback))
        name += stageData?.gain.target > 1 ? 'Gain' : '';
    }
    const spicePart = spiceParts[opAmpName];
    if (spicePart) {
      name += '_' + spicePart.symbolPath.slice(8);
      schematicContent = schematicContent.replace('subckt_stage' + (i + 1) + '_stageType_deviceNumber', name);
    }
  }
  return new AditoolsSpice.SpiceSchematic(spiceParts, schematicContent);
}

function getSchematicValues(circuit: CircuitDetailResults): Record<string, string | number> {
  const schematicValues: Record<string, string | number> = {};
  if (circuit.c1 && circuit.c1.value !== null) {
    schematicValues['C1'] = circuit.c1.value ?? '';
  }
  if (circuit.c2 && circuit.c2.value !== null) {
    schematicValues['C2'] = circuit.c2.value ?? '';
  }
  if (circuit.c5 && circuit.c5.value !== null) {
    schematicValues['C5'] = circuit.c5.value ?? '';
  }
  if (circuit.r1 && circuit.r1.value !== null) {
    schematicValues['R1'] = circuit.r1.value ?? '';
  }
  if (circuit.r2 && circuit.r2.value !== null && !isNearInfinity(circuit.r2.value)) {
    schematicValues['R2'] = circuit.r2.value ?? '';
  }
  if (circuit.r3 && circuit.r3.value !== null && circuit.r4 && circuit.r4.value !== null && isGainCircuit(circuit)) {
    schematicValues['R3'] = circuit.r3.value ?? '';
    schematicValues['R4'] = circuit.r4.value ?? '';
  }
  if (circuit.r5 && circuit.r5.value !== null) {
    schematicValues['R5'] = circuit.r5.value ?? '';
  }
  if (circuit.l1 && circuit.l1.value !== null) {
    schematicValues['L1'] = circuit.l1.value ?? '';
  }
  return schematicValues;
}

function isGainCircuit(circuit: CircuitDetailResults): boolean {
  if (circuit.r3.value !== null && circuit.r4.value !== null) {
    if (circuit.implementation === FilterConstants.implementationType.multipleFeedback) {
      return false;
    } else if (circuit.implementation === FilterConstants.implementationType.deliyannisFriend) {
      return !(circuit.r3.value === 0 && isNearInfinity(circuit.r4.value));
    } else {
      return !(isNearInfinity(circuit.r3.value) && circuit.r4.value === 0);
    }
  }
  return false;
}

function generateReadMeFile(
  opAmpsList: Array<string>,
  netlistParts: Array<SpicePartsDownloadType>,
  schematicParts: Array<SpicePartsDownloadType>,
  hasLTspice: boolean,
  downloadType: string,
  intlService: IntlService,
): Array<DownloadFile> {
  const result: Array<DownloadFile> = [];

  const listOfParts = mergePartsArray(netlistParts, schematicParts, 'Netlist', 'LTspice');
  const opAmpNoSpice = opAmpsList.filter((opAmpName) => {
    const searchpart = listOfParts.find(function (item) {
      return item.partName === opAmpName;
    });
    return typeof searchpart === 'undefined' || searchpart === null;
  });
  const noSpiceFiles = opAmpNoSpice.length > 0;
  const opAmpsNoNetlist = listOfParts.find(function (item) {
    return !item.type.includes('Netlist');
  });
  const hasNetList = opAmpsNoNetlist === undefined;
  if (noSpiceFiles) {
    // No netlist or spice schematics generated because one or more parts never supported both  (Scenario C)
    const fileMessageName = 'Why no spice files.txt';
    let fileMessageContent = intlService.t('download.file-content.part-no-spice-info').toString();

    // Add list of Non-Supported Parts
    for (let k = 0; k < opAmpNoSpice.length; k++) {
      fileMessageContent += '\r\n' + opAmpNoSpice[k];
    }
    // Add Message File
    result.push({ name: fileMessageName, value: fileMessageContent, location: '', type: 'LTSPICE' });
  } else {
    if (!hasLTspice) {
      // No spice or netlist files because the combination of parts could not build either of them. (Scenario B)
      // Or design or only contains Netlist files (Scenario A)
      const fileMessageName = hasNetList ? 'Why no LTspice.txt' : 'readme.txt';
      let fileMessageContent = hasNetList
        ? intlService.t('download.file-content.no-schematic').toString()
        : intlService.t('download.file-content.no-schematic-no-netlist').toString();

      // Add list of Supported Parts
      for (let k = 0; k < listOfParts.length; k++) {
        fileMessageContent += '\r\n' + listOfParts[k]?.partName + ': ' + listOfParts[k]?.type;
      }
      // Add Message File
      result.push({
        name: fileMessageName,
        value: fileMessageContent,
        location: downloadType === 'all' ? 'SPICE Files' : '',
        type: 'LTSPICE',
      });
    }
  }
  return result;
}
